/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  dedupeEvaluationsToLatestVersions,
  resolveRubricVersion
} from '../../../../../common/util/evaluation/evalRubricVersionUtils';
import { DATE_UNIT_DAY } from '../../../../../common/constants';
import {
  getBrowserTimeZone,
  normalizeCustomEvalSummary
} from '../../../../../common/util/evaluation/evalCustomRangeUtils';
import * as studioConstants from '../../../constants/StudioTextConstants';

export { dedupeEvaluationsToLatestVersions, resolveRubricVersion, getBrowserTimeZone };

dayjs.extend(utc);
dayjs.extend(timezone);

const SAMPLE_PERCENT_INPUT_PATTERN = /^\d{1,3}$/;

export const LOCAL_HOURS = Array.from({ length: 24 }, (_, i) => i);
export const LOCAL_MINUTES = Array.from({ length: 60 }, (_, i) => i);

const pad2 = (n) => String(n).padStart(2, '0');

/** UI always uses daily; legacy weekly/monthly values are normalized on read and save. */
export function normalizeEvalSchedule() {
  return studioConstants.EVAL_SCHEDULE_DAILY;
}

export const formatEvalTimeLocal = (hour, minute) => `${pad2(hour)}:${pad2(minute)}:00`;

export const normalizeScheduler = (value) => {
  const dateTime = String(value?.dateTime ?? '').trim();
  const timeZone = String(value?.timeZone ?? '').trim();
  if (!dateTime || !timeZone) {
    return null;
  }
  return { dateTime, timeZone };
};

export const schedulerToDayjsLocal = (scheduler) => {
  const normalized = normalizeScheduler(scheduler);
  if (!normalized) {
    return null;
  }
  const zoned = dayjs.tz(normalized.dateTime, studioConstants.DateFormat.SCHEDULER_DATETIME, normalized.timeZone);
  return zoned.isValid() ? zoned.local() : null;
};

export const dayjsLocalToScheduler = (localValue, timeZone = getBrowserTimeZone()) => {
  if (!localValue?.isValid?.()) {
    return null;
  }
  return {
    dateTime: localValue.format(studioConstants.DateFormat.SCHEDULER_DATETIME),
    timeZone
  };
};

export const isSameScheduleInstant = (left, right) => {
  const leftLocal = schedulerToDayjsLocal(left);
  const rightLocal = schedulerToDayjsLocal(right);
  if (!leftLocal?.isValid?.() || !rightLocal?.isValid?.()) {
    return false;
  }
  return leftLocal.valueOf() === rightLocal.valueOf();
};

export const resolveSchedulerForSave = ({ draftLocal, storedScheduler }) => {
  const draftScheduler = dayjsLocalToScheduler(draftLocal);
  if (!draftScheduler) {
    return null;
  }
  if (storedScheduler && isSameScheduleInstant(draftScheduler, storedScheduler)) {
    return normalizeScheduler(storedScheduler);
  }
  return draftScheduler;
};

export const scheduleFromRubricData = (data) => ({
  scheduler: normalizeScheduler(data?.scheduler),
  evalSchedule: normalizeEvalSchedule(),
  recurring: !!data?.recurring,
  isConversational: !!data?.isConversational
});

export function formatSamplePercentForDisplay(samplePercent) {
  if (samplePercent === '' || samplePercent == null) {
    return '';
  }
  return String(samplePercent);
}

export function normalizeSamplePercentFromApi(samplePercent) {
  if (samplePercent !== '' && samplePercent != null) {
    return String(samplePercent);
  }
  return studioConstants.EVAL_CRITERIA_DEFAULT_SAMPLE_PERCENT;
}

export function clampSamplePercent(value) {
  const n = Number(value);
  if (Number.isNaN(n)) {
    return studioConstants.EVAL_SAMPLE_PERCENT_MIN;
  }
  return Math.min(studioConstants.EVAL_SAMPLE_PERCENT_MAX, Math.max(studioConstants.EVAL_SAMPLE_PERCENT_MIN, n));
}

/** @returns {string|null} Next sample % string, or null if input should be ignored */
export function parseSamplePercentInput(rawValue) {
  if (rawValue === '') {
    return '';
  }
  if (!SAMPLE_PERCENT_INPUT_PATTERN.test(rawValue)) {
    return null;
  }
  const n = parseInt(rawValue, 10);
  return String(clampSamplePercent(n));
}

export const splitEvalTypeOptionsForPicker = (options) => {
  if (!Array.isArray(options) || options.length <= studioConstants.EVAL_TYPES_SINGLE_TABLE_MAX) {
    return { left: options || [], right: [] };
  }
  const splitAt = Math.ceil(options.length / 2);
  return { left: options.slice(0, splitAt), right: options.slice(splitAt) };
};

export const formatEvalTypesFieldDisplay = ({ selectedEvalTypes, agentTypeOptions, formatMessage, getText }) => {
  if (!agentTypeOptions.length) {
    return '';
  }
  if (selectedEvalTypes.length === agentTypeOptions.length) {
    return formatMessage(getText('graphEvaluation.ALL_TYPES'));
  }
  if (!selectedEvalTypes.length) {
    return formatMessage(getText('graphEvaluation.TYPES_PLACEHOLDER'));
  }
  return formatMessage(getText('graphEvaluation.TYPES_SELECTED'), { count: selectedEvalTypes.length });
};

const normalizeEvalTypesList = (evalTypes) => {
  if (!Array.isArray(evalTypes)) {
    return [];
  }
  return [...evalTypes].sort();
};

export const isRubricSnapshotDirty = ({ markdown, evalTypes, savedMarkdown, savedEvalTypes }) => {
  const currentMarkdown = typeof markdown === 'string' ? markdown : '';
  const baselineMarkdown = typeof savedMarkdown === 'string' ? savedMarkdown : '';
  if (currentMarkdown !== baselineMarkdown) {
    return true;
  }

  const currentTypes = normalizeEvalTypesList(evalTypes);
  const savedTypes = normalizeEvalTypesList(savedEvalTypes);
  if (currentTypes.length !== savedTypes.length) {
    return true;
  }
  return currentTypes.some((value, index) => value !== savedTypes[index]);
};

/** List / summary display only — edit form keeps raw empty rubricName for legacy rows. */
export const formatRubricNameForList = (rubricName) => {
  const trimmed = String(rubricName ?? '').trim();
  return trimmed || studioConstants.LEGACY_RUBRIC_LIST_DISPLAY_NAME;
};

export const normalizeEvaluationListItem = (item) => {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const rubricId = item.rubricId != null && item.rubricId !== '' ? String(item.rubricId) : null;
  if (!rubricId) {
    return null;
  }
  const scheduler = normalizeScheduler(item.scheduler);
  return {
    rubricId,
    rubricVersion: resolveRubricVersion(item.rubricVersion),
    rubricName: String(item.rubricName ?? '').trim(),
    evalTypes: Array.isArray(item.evalTypes) ? item.evalTypes : [],
    evalSamplePercent: item.evalSamplePercent ?? item.samplePercent ?? '',
    scheduler,
    recurring: !!item.recurring,
    isConversational: !!item.isConversational,
    isScheduled: item.isScheduled != null ? !!item.isScheduled : !!scheduler,
    customEval: normalizeCustomEvalSummary(item.customEval)
  };
};

export const normalizeEvaluationDetail = (item) => {
  const base = normalizeEvaluationListItem(item);
  if (!base) {
    return null;
  }
  const evalRubric = item?.evalRubric ?? item?.rubricText ?? '';
  return {
    ...base,
    evalRubric: typeof evalRubric === 'string' ? evalRubric : '',
    samplePercent: normalizeSamplePercentFromApi(item?.evalSamplePercent ?? item?.samplePercent)
  };
};

export const upsertEvaluationSummary = (summaries, { rubricId, rubricName }) => {
  if (!rubricId) {
    return summaries ?? [];
  }
  const next = Array.isArray(summaries) ? [...summaries] : [];
  const index = next.findIndex((item) => String(item.rubricId) === String(rubricId));
  const entry = { rubricId: String(rubricId), rubricName: String(rubricName ?? '').trim() };
  if (index >= 0) {
    next[index] = { ...next[index], ...entry };
  } else {
    next.push(entry);
  }
  return next;
};

export const removeEvaluationSummary = (summaries, rubricId) => {
  if (!rubricId) {
    return summaries ?? [];
  }
  return (Array.isArray(summaries) ? summaries : []).filter((item) => String(item.rubricId) !== String(rubricId));
};

export const formatEvaluationListScheduleSummary = ({ scheduler, isScheduled }, formatMessage, getText) => {
  const scheduled = isScheduled != null ? isScheduled : !!normalizeScheduler(scheduler);
  if (!scheduled || !scheduler) {
    return formatMessage(getText('graphEvaluation.LIST_NOT_SCHEDULED'));
  }
  const local = schedulerToDayjsLocal(scheduler);
  if (!local?.isValid?.()) {
    return formatMessage(getText('graphEvaluation.LIST_SCHEDULED'));
  }
  const timeLabel = local.format('MMM D, YYYY h:mm A');
  return formatMessage(getText('graphEvaluation.LIST_SCHEDULE_AT'), { time: timeLabel });
};

export const resolveEvaluationListScheduleBadge = ({ scheduler, recurring, isScheduled }) => {
  const scheduled = isScheduled != null ? isScheduled : !!normalizeScheduler(scheduler);
  if (!scheduled || !scheduler) {
    return null;
  }
  const local = schedulerToDayjsLocal(scheduler);
  if (!local?.isValid?.()) {
    return null;
  }
  return recurring ? 'daily' : 'oneTime';
};

export const formatEvaluationListTypesSummary = (evalTypes, formatMessage, getText) => {
  const count = Array.isArray(evalTypes) ? evalTypes.length : 0;
  if (!count) {
    return formatMessage(getText('graphEvaluation.LIST_TYPES_NONE'));
  }
  return formatMessage(getText('graphEvaluation.LIST_TYPES_COUNT'), { count });
};

export const isDetailFormDirty = ({
  rubricName,
  markdown,
  evalTypes,
  savedRubricName,
  savedMarkdown,
  savedEvalTypes
}) => {
  if (String(rubricName ?? '').trim() !== String(savedRubricName ?? '').trim()) {
    return true;
  }
  return isRubricSnapshotDirty({ markdown, evalTypes, savedMarkdown, savedEvalTypes });
};

export const buildCreateEvaluationRequestBody = ({ rubricName, evalRubric, evalTypes, isRubricValidated }) => {
  const requestBody = {
    rubricName: String(rubricName ?? '').trim(),
    evalRubric,
    evalTypes
  };
  if (isRubricValidated !== undefined) {
    requestBody.isRubricValidated = !!isRubricValidated;
  }
  return requestBody;
};

export const buildRubricTextRequestBody = ({ rubricName, evalRubric, evalTypes, isRubricValidated }) => {
  const requestBody = {
    rubricName: String(rubricName ?? '').trim(),
    evalRubric,
    evalTypes
  };
  if (isRubricValidated !== undefined) {
    requestBody.isRubricValidated = !!isRubricValidated;
  }
  return requestBody;
};

/** Normalize create/update rubric success payloads ({ success, data: { rubricId, ... } }). */
export const parseSavedRubricEvent = (event, fallbackRubricId = null) => {
  const envelope = event?.data ?? null;
  const body =
    envelope?.data != null && typeof envelope.data === 'object' && !Array.isArray(envelope.data)
      ? envelope.data
      : envelope ?? event ?? {};

  const rubricIdRaw = event?.rubricId ?? body?.rubricId ?? fallbackRubricId ?? null;
  const rubricId = rubricIdRaw != null && rubricIdRaw !== '' ? String(rubricIdRaw) : null;

  const rubricVersionRaw = event?.rubricVersion ?? body?.rubricVersion ?? null;

  return {
    rubricId,
    rubricVersion: rubricVersionRaw != null ? resolveRubricVersion(rubricVersionRaw) : null,
    rubricName: event?.rubricName ?? body?.rubricName ?? '',
    evalRubric: event?.evalRubric ?? body?.evalRubric ?? body?.rubricText ?? null,
    evalTypes: event?.evalTypes ?? body?.evalTypes ?? null
  };
};

/** Merge partial evaluation fields into settings cache (replaces former otherParams.evaluation). */
export function mergeEvaluationCriteriaCache(current, partial) {
  if (!partial) {
    return current ?? null;
  }
  return {
    evalRubric: partial.evalRubric ?? current?.evalRubric ?? '',
    rubricName: partial.rubricName ?? current?.rubricName ?? '',
    samplePercent: partial.samplePercent ?? current?.samplePercent ?? '',
    rubricId: partial.rubricId ?? current?.rubricId ?? null,
    evalTypes: partial.evalTypes ?? current?.evalTypes ?? [],
    evalSchedule: partial.evalSchedule !== undefined ? partial.evalSchedule : current?.evalSchedule ?? null,
    recurring: partial.recurring !== undefined ? partial.recurring : !!current?.recurring,
    isConversational: partial.isConversational !== undefined ? !!partial.isConversational : !!current?.isConversational,
    scheduler: partial.scheduler !== undefined ? partial.scheduler : current?.scheduler ?? null
  };
}

export const buildSchedulerRequestBody = ({
  evalSamplePercent,
  scheduler,
  recurring,
  isConversational,
  draftLocal,
  storedScheduler
}) => ({
  evalSamplePercent,
  scheduler: resolveSchedulerForSave({ draftLocal, storedScheduler }) ?? normalizeScheduler(scheduler),
  recurring: !!recurring,
  isConversational: !!isConversational,
  evalSchedule: normalizeEvalSchedule()
});

export const localTodayStart = () => dayjs().startOf(DATE_UNIT_DAY);

export const isLocalDateBeforeToday = (localDate) =>
  localDate?.isValid?.() ? localDate.isBefore(localTodayStart(), DATE_UNIT_DAY) : false;

export const isLocalScheduleInPast = (localDateTime) =>
  localDateTime?.isValid?.() ? localDateTime.isBefore(dayjs(), 'minute') : false;

export const getAvailableLocalHours = (localDate) => {
  const today = localTodayStart();
  if (!localDate?.isValid?.() || !localDate.isSame(today, DATE_UNIT_DAY)) {
    return LOCAL_HOURS;
  }
  return LOCAL_HOURS.filter((h) => h >= dayjs().hour());
};

export const getAvailableLocalMinutes = (localDate, hour) => {
  const now = dayjs();
  if (!localDate?.isValid?.() || !localDate.isSame(now, DATE_UNIT_DAY)) {
    return LOCAL_MINUTES;
  }
  if (hour > now.hour()) return LOCAL_MINUTES;
  if (hour < now.hour()) return [];
  return LOCAL_MINUTES.filter((m) => m >= now.minute());
};

export const buildLocalDateTimeFromDraft = ({ draftDate, draftHour, draftMinute }) => {
  if (!draftDate?.isValid?.()) {
    return null;
  }
  return draftDate.hour(draftHour).minute(draftMinute).second(0).millisecond(0);
};

/**
 * Modal OK validation. Returns an i18n key or null.
 */
export function getScheduleModalOkErrorKey({ isNewSchedule, opened, draftLocal, draftRecurring }) {
  const openedLocal = schedulerToDayjsLocal(opened.scheduler);
  const dateChanged = !openedLocal?.isValid?.() ? true : !draftLocal.isSame(openedLocal, DATE_UNIT_DAY);
  const timeChanged = !openedLocal?.isValid?.() ? true : !draftLocal.isSame(openedLocal, 'minute');
  const recurringChanged = !!opened.recurring !== !!draftRecurring;

  if (dateChanged && isLocalDateBeforeToday(draftLocal)) {
    return 'graphEvaluation.PAST_DATE_ERROR';
  }

  if (isNewSchedule) {
    return isLocalScheduleInPast(draftLocal) ? 'graphEvaluation.CURRENT_OR_FUTURE_DATETIME_ERROR' : null;
  }

  if (timeChanged && !recurringChanged && opened.recurring && draftRecurring) {
    return null;
  }

  if (recurringChanged && isLocalScheduleInPast(draftLocal)) {
    return 'graphEvaluation.CURRENT_OR_FUTURE_DATETIME_ERROR';
  }

  if (!draftRecurring && (dateChanged || timeChanged) && isLocalScheduleInPast(draftLocal)) {
    return 'graphEvaluation.CURRENT_OR_FUTURE_DATETIME_ERROR';
  }

  return null;
}

export const hasScheduleComplete = (schedule) => !!normalizeScheduler(schedule?.scheduler);

export const buildScheduleSnapshot = ({ scheduler, evalSchedule }) => ({
  scheduler: normalizeScheduler(scheduler),
  evalSchedule: evalSchedule ?? null
});

const ENABLED_BUTTON_STATE = Object.freeze({ disabled: false, tooltipKey: null });

/** Disabled state when the sample % is missing or out of range, otherwise null. */
const getSamplePercentButtonState = (samplePercent) => {
  const pct = Number(samplePercent);
  if (
    samplePercent === '' ||
    samplePercent == null ||
    Number.isNaN(pct) ||
    pct < studioConstants.EVAL_SAMPLE_PERCENT_MIN ||
    pct > studioConstants.EVAL_SAMPLE_PERCENT_MAX
  ) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SCHEDULE_TOOLTIP_MISSING_SAMPLE' };
  }
  return null;
};

export function getScheduleButtonStateWithSample({
  samplePercent,
  scheduler,
  recurring,
  rubricId,
  saveDisabled,
  hasOnSchedule,
  isSaving
}) {
  if (saveDisabled || !hasOnSchedule || isSaving) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SCHEDULE' };
  }

  if (!rubricId) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SCHEDULE_TOOLTIP_SAVE_RUBRIC_FIRST' };
  }

  if (!normalizeScheduler(scheduler)) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SCHEDULE_TOOLTIP_SELECT_DATETIME' };
  }

  const sampleState = getSamplePercentButtonState(samplePercent);
  if (sampleState) {
    return sampleState;
  }

  const localSchedule = schedulerToDayjsLocal(scheduler);
  if (!recurring && localSchedule && isLocalScheduleInPast(localSchedule)) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SCHEDULE_TOOLTIP_SELECT_FUTURE_DATETIME' };
  }

  return ENABLED_BUTTON_STATE;
}

/**
 * Run Now button state for a selected past range: needs a saved rubric and a valid sample %.
 * @returns {{ disabled: boolean, tooltipKey: string|null }}
 */
export function getCustomEvalButtonState({ samplePercent, rubricId, saveDisabled, hasOnCustomEval, isSaving }) {
  if (saveDisabled || !hasOnCustomEval || isSaving) {
    return { disabled: true, tooltipKey: 'customEval.RUN_NOW' };
  }

  if (!rubricId) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SCHEDULE_TOOLTIP_SAVE_RUBRIC_FIRST' };
  }

  return getSamplePercentButtonState(samplePercent) ?? ENABLED_BUTTON_STATE;
}

const getRubricTextValidationState = ({ markdown, evalTypes }) => {
  const hasMarkdown = typeof markdown === 'string' && markdown.trim().length > 0;
  if (!hasMarkdown) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SAVE_TOOLTIP_MISSING_RUBRIC_TEXT' };
  }

  if (!Array.isArray(evalTypes) || evalTypes.length === 0) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SAVE_TOOLTIP_MISSING_EVAL_TYPES' };
  }

  return { disabled: false, tooltipKey: null };
};

/**
 * Enhance rubric button disabled state and hover tooltip key.
 * @returns {{ disabled: boolean, tooltipKey: string|null }}
 */
export function getRubricEnhanceButtonState({
  markdown,
  evalTypes,
  saveDisabled,
  hasOnEnhance,
  isEnhancing,
  isEnhanceReviewOpen
}) {
  if (saveDisabled || !hasOnEnhance || isEnhancing || isEnhanceReviewOpen) {
    return { disabled: true, tooltipKey: 'graphEvaluation.ENHANCE' };
  }

  return getRubricTextValidationState({ markdown, evalTypes });
}

/**
 * Save rubric text button disabled state and hover tooltip key.
 * @returns {{ disabled: boolean, tooltipKey: string|null }}
 */
export function getRubricSaveButtonState({
  rubricName,
  markdown,
  evalTypes,
  saveDisabled,
  hasOnSave,
  isSaving,
  isEnhanceReviewOpen,
  savedRubricName,
  savedMarkdown,
  savedEvalTypes
}) {
  if (isEnhanceReviewOpen) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SAVE_TOOLTIP_ENHANCE_REVIEW' };
  }

  if (saveDisabled || !hasOnSave || isSaving) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SAVE' };
  }

  if (!String(rubricName ?? '').trim()) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SAVE_TOOLTIP_MISSING_RUBRIC_NAME' };
  }

  const validationState = getRubricTextValidationState({ markdown, evalTypes });
  if (validationState.disabled) {
    return validationState;
  }

  if (
    !isDetailFormDirty({
      rubricName,
      markdown,
      evalTypes,
      savedRubricName,
      savedMarkdown,
      savedEvalTypes
    })
  ) {
    return { disabled: true, tooltipKey: 'graphEvaluation.SAVE_TOOLTIP_NOT_DIRTY' };
  }

  return { disabled: false, tooltipKey: null };
}

export function normalizePercentForSave(samplePercent) {
  if (samplePercent === '' || samplePercent == null) {
    return studioConstants.EVAL_CRITERIA_DEFAULT_SAMPLE_PERCENT;
  }
  return clampSamplePercent(samplePercent);
}
